<template>
  <v-card>
    <v-card-title>
      <ent-workflow-nav-bar
        :args="wNav"
        :buttonclick.sync="navbuttons"
        :title="wTitle"
      ></ent-workflow-nav-bar>
    </v-card-title>
    <v-card-text>
      <v-row no-gutters dense>
        <schema-form
          :args="mainPanelArgs"
          :saveok.sync="mainPanelSave"
          :formStyle="mainPanelStyle"
          v-model="mainPanelData"
          :key="mainPanelKey"
          v-if="mainPanelReady"
        ></schema-form>
      </v-row>
      <v-row>
        <ent-workflow-welcome :args="sampleName" :formStyle="mainPanelStyle">
        </ent-workflow-welcome>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script setup>
  // Components used in this view
  import EntWorkflowNavBar from "@/../../library/view/EntWorkflowNavBar.vue";
  import EntWorkflowWelcome from "@/../../library/view/EntWorkflowWelcome.vue";
  import EntSearchConfig from "@/../../library/view/EntSearchConfig.vue";
  import SchemaForm from "@/../../library/view/SchemaForm.vue";

  // Component functions
  import { useWorkflowController } from "@/../../library/compFunctions/useWorkflowController.js";
  import {
    fullSize,
    card800,
    fieldStyles,
  } from "@/../../library/compFunctions/useStyles.js";
  import {
    startToast,
    endToast,
  } from "@/../../cortex/src/compFunctions/useToasted.js";

  // Services used
  import {
    create,
    update,
    loadOneActive,
    findOne,
  } from "@/../../cortex/src/services/cortex.js";

  // Helper Functions
  import check from "@/../../library/helper/propertyCheck.js";
  const CONSTANTS = require("@/../../library/constants/server.js");
  import moment from "moment";

  // Vue helper functions
  import { onMounted, ref, watch, getCurrentInstance, computed } from "vue";

  // Define the properties
  const props = defineProps({ args: { type: Object, required: false } });

  // Define the emits of this flow
  const emit = defineEmits(["update:finished"]);

  // Define the variables for the main panel
  let mainPanelSave = ref(false);
  let mainPanelArgs = ref({
    schema: "sch_name_config",
    datatable: "fac_settings",
    item: "",
    dataserver: CONSTANTS.API,
  });
  let mainPanelFinish = ref(null);
  let mainPanelStyle = ref(null);
  let mainPanelData = ref({});
  let mainPanelDataSet = ref({});
  let mainPanelKey = ref(0)
  let mainPanelReady = ref(false)

  // Get the VM instance - needed for some of the plugins
  const vm = getCurrentInstance();
  console.log(`******** VM = `, vm);

  // Prepare the workflow configuration based on the controller properties
  const {
    config: wNav,
    mode: mode,
    hero: heroPanel,
    search: searchPanel,
    main: mainPanel,
  } = useWorkflowController();

  let wTitle = ref("");
  let sampleRoom = ref(null);
  let sampleStrain = ref(null);

  // Search conditions for finding the record for this setting
  let searchConditions = [{"$match": {"IsUsed": "yes"}}, {"$match": {"key":"batch_name"}}]
  // Set the default state of the widget on mount
  onMounted(async () => {
    console.log(`%%%%% Immature Naming Top Level Mounted`);
    wTitle.value = props.args?.title;
    mainPanelStyle.value = fullSize();
    // Check for an existing version of this setting
    let existingSettingTemp = findOne(searchConditions, "fac_settings")
    sampleRoom.value = await loadOneActive("set_rooms");
    sampleStrain.value = await loadOneActive("set_strain");

    let existingSetting = await existingSettingTemp
    console.log(`EXISTING CHECK RESULT = `, existingSetting)
    if(check.isNull(existingSetting)) mode.value = "create"
    else {
      mainPanelArgs.value.item = existingSetting["_id"];
      mode.value = "edit"
    }
    mainPanelKey.value++
    mainPanelReady.value = true
  });

  // Create the variable to handle the navigation bar button clicks
  const navbuttons = ref(null);

  // Process actions when save is enabled
  watch(mainPanelSave, () => {
    mode.value = "saveOK";
    console.log(`Save OK - calling the sample name computation`);
    computeSampleName();
  });

  // Compute the same name
  const sampleNameLabel = "Sample Name";
  const sampleNameStyle = fieldStyles("string", false, true);
  const sampleName = ref("");
  const sampleNameKey = ref(0);

  const computeSampleName = () => {
    console.log(`Sample Name computation params = `, mainPanelData);
    if (check.isUndefined(mainPanelData.value.payload.fields)) return "";
    let name = "";
    // Add the prefix
    if (mainPanelData.value.payload.hasOwnProperty("pre"))
      name = mainPanelData.value.payload.pre;
    // Add the fields to the name
    for (const entry of mainPanelData.value.payload.fields) {
      if (entry === "Strain") name = `${name} ${sampleStrain.value.name}`;
      if (entry === "Room") name = `${name} ${sampleRoom.value.name}`;
      if (entry === "Room Initials") name = `${name} ${sampleRoom.value.ri}`;
      if (entry === "Zone") name = `${name} ${sampleRoom.value.zone}`;
      if (entry === "Date") name = `${name} ${moment().format("MM-DD-YYYY")}`;
      console.log(`Fields Loops name = ${name}`);
    }
    // Add the postfix
    if (mainPanelData.value.payload.hasOwnProperty("post"))
      name = `${name} ${mainPanelData.value.payload.post}`;

    // Add the number offset
    name = `${name} - 1`;
    sampleName.value = `Sample Immature Batch Name: ${name}`;
    console.log(`Sample Name = ${name}`);
    sampleNameKey.value++;
  };

  // Process what happens when a button is clicked
  watch(navbuttons, async () => {
    let button = navbuttons.value.click;
    console.log(`Nav Button Click `, button);
    if (button === "cancel") cancelClick();
    else if (button === "save") await saveClick();
  });

  const saveClick = async () => {
    console.log(`@@@@@@ Save Clicked, data: `, mainPanelData.value.payload);
    if (!mainPanelData.value.payload.hasOwnProperty("_id"))
      await createSetting();
    else {
      console.log(`%%%%%%% Update of an existing Record`);
      await updateSetting();
    }
    cancelClick();
  };

  // Close Button Click
  const cancelClick = () => {
    vm.proxy.$router.go(-1);
  };

  // Functions to work with the record
  // Generate the naming map
  const namingMap = (data) => {
    let map = [];
    if (data.hasOwnProperty("pre")) {
      let entry = { key: "pre", src: "string", value: data.pre };
      map.push(entry);
    }
    // Add the fields into the map
    for (const entry of data.fields) {
      let obj = { src: "table" };
      if (entry === "Strain") {
        obj["key"] = "name";
        obj["value"] = "set_strain";
      }
      if (entry === "Room") {
        obj["key"] = "name";
        obj["value"] = "set_rooms";
      }
      if (entry === "Room Initials") {
        obj["key"] = "ri";
        obj["value"] = "set_rooms";
      }
      if (entry === "Zone") {
        obj["key"] = "zone";
        obj["value"] = "set_rooms";
      }
      if (entry === "Date") {
        obj["key"] = "date";
        obj["src"] = "date";
        obj["format"] = "MM/DD/YYYY"
      }
      map.push(obj);
    }
    if (data.hasOwnProperty("post")) {
      let entry = { key: "post", src: "string", value: data.post };
      map.push(entry);
    }
    return map;
  };
  // Function to save a note
  const createSetting = async () => {
    let payload = mainPanelData.value.payload;
    console.log(`Payload = `, payload);
    // Create the order map
    payload["map"] = namingMap(payload);
    payload["key"] = "batch_name";
    startToast(vm, "Saving New Record");
    let saveResult = await create(mainPanelArgs.value.datatable, payload);
    //console.log(`Trying to Stop the loader`);
    endToast(vm, "Record Saved");
  };

  // Function to update a note
  const updateSetting = async () => {
    let payload = mainPanelData.value.payload;
    // Create the order map
    payload["map"] = namingMap(payload);
    payload["key"] = "batch_name";
    startToast(vm, "Saving Record Update");
    let updateResult = await update(mainPanelArgs.value.datatable, payload);
    endToast(vm, "Record Updated");
  };
</script>
